import { resolveNotificationTarget } from "./notificationTarget";

/**
 * Where a signed-out visitor was trying to go, held across sign-in. Every
 * sign-in exit hard-codes /dashboard, so without this an emailed notification
 * link opened while signed out would always land on the dashboard.
 *
 * sessionStorage: per tab, survives the OAuth round trip and the native
 * social sign-in's full reload, and is gone when the tab is.
 */

const STORAGE_KEY = "insight:return-destination";
const MAX_AGE_MS = 30 * 60 * 1000;

export const rememberReturnDestination = (path: string): void => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path, expiresAt: Date.now() + MAX_AGE_MS }),
    );
  } catch {
    // Storage unavailable — sign-in lands on the dashboard, as it always has
  }
};

export const clearReturnDestination = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear
  }
};

/**
 * The remembered path, only if it is unexpired AND passes the same guard a
 * notification destination does — sessionStorage is writable by anything that
 * can run script on the origin, so its contents are input, not state.
 */
export const readReturnDestination = (isNative: boolean): string | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== "object" || stored === null) return null;
    const { path, expiresAt } = stored as { path?: unknown; expiresAt?: unknown };
    if (typeof path !== "string" || typeof expiresAt !== "number") return null;
    if (Date.now() >= expiresAt) return null;
    const resolved = resolveNotificationTarget(path, null, { isNative });
    return resolved.kind === "internal" ? resolved.path : null;
  } catch {
    return null;
  }
};
